import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, FileText, Archive, Target, Users, Sparkles } from "lucide-react";

import Header from "../components/Header";
import PaywallBanner from "../components/PaywallBanner";
import PromoBanner from "../components/PromoBanner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

function StatusBanner({ tone, title, body, action, testId }) {
  const toneMap = {
    neutral: "border-slate-200 bg-white",
    warning: "border-amber-200 bg-amber-50",
    danger: "border-rose-200 bg-rose-50",
    success: "border-emerald-200 bg-emerald-50",
    info: "border-indigo-200 bg-indigo-50",
  };
  return (
    <section
      className={`rounded-2xl border ${toneMap[tone] || toneMap.neutral} p-6 shadow-sm`}
      data-testid={testId || `dashboard-banner-${tone}`}
    >
      <h2 className="text-base font-semibold text-slate-900 md:text-lg">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </section>
  );
}

function StatCard({ label, value, testId, href, hint, Icon }) {
  const inner = (
    <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
      {Icon && (
        <div className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
        {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user, organization } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [billingSub, setBillingSub] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "agency") return;
    if (organization?.status !== "active") return;
    (async () => {
      try {
        const { data } = await api.get("/billing/subscription");
        setBillingSub(data);
      } catch (_) { /* silent */ }
    })();
  }, [user, organization]);

  useEffect(() => {
    if (!user) return;
    if ((user.role === "employer" && organization?.status === "active") || (user.role === "agency" && organization?.status === "active")) {
      (async () => {
        try {
          if (user.role === "employer") {
            const { data } = await api.get("/missions/mine");
            const s = { active: 0, drafts: 0, closed: 0 };
            for (const m of data) {
              if (m.status === "draft") s.drafts++;
              else if (m.status === "open" || m.status === "shortlisting") s.active++;
              else s.closed++;
            }
            setStats(s);
          } else if (user.role === "agency") {
            const subActive = organization?.subscription_status === "active" || organization?.subscription_status === "trialing";
            const specs = organization?.specialties || [];
            const marketPromise = subActive
              ? (specs.length
                  ? api.get("/missions/marketplace", { params: { skills: specs.join(",") } })
                  : api.get("/missions/marketplace"))
              : Promise.resolve({ data: [] });
            const [mine, market, roster] = await Promise.all([
              api.get("/missions/mine"),
              marketPromise,
              api.get("/consultants", { params: { status: "active" } }),
            ]);
            setStats({
              my_open: mine.data.filter((m) => m.status === "open" || m.status === "shortlisting").length,
              market_matches: market.data.length,
              roster: roster.data.length,
            });
          }
        } catch (err) {
          // silent — dashboard still renders; log for debugging.
          console.warn("Dashboard stats fetch failed:", err);
        }
      })();
    }
  }, [user, organization]);

  if (!user) return null;
  if (user.role === "admin") return <Navigate to="/admin/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <PaywallBanner />
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
            {t(`dashboard.role_badge_${user.role}`)}
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl" data-testid="dashboard-welcome-title">
            {user.role === "admin"
              ? t("dashboard.welcome_admin")
              : t(`dashboard.welcome_${user.role}`, { name: user.name })}
          </h1>
        </div>

        {user.role === "employer" && organization?.status === "pending_approval" && (
          <StatusBanner
            tone="warning"
            title={t("dashboard.employer_pending_title")}
            body={t("dashboard.employer_pending_body")}
            testId="employer-pending-banner"
          />
        )}
        {user.role === "employer" && organization?.status === "rejected" && (
          <StatusBanner
            tone="danger"
            title={t("dashboard.employer_rejected_title")}
            body={t("dashboard.employer_rejected_body")}
            testId="employer-rejected-banner"
          />
        )}
        {user.role === "employer" && organization?.status === "active" && (
          <>
            {stats && (
              <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="employer-stats">
                <StatCard Icon={Briefcase} label={t("dashboard.stat_active_missions")} value={stats.active} testId="stat-active" href="/missions/mine" />
                <StatCard Icon={FileText} label={t("dashboard.stat_drafts")} value={stats.drafts} testId="stat-drafts" href="/missions/mine" />
                <StatCard Icon={Archive} label={t("dashboard.stat_closed")} value={stats.closed} testId="stat-closed" href="/missions/mine" />
              </div>
            )}
            <StatusBanner
              tone="neutral"
              title={t("dashboard.employer_ready_title")}
              body={t("dashboard.employer_ready_body")}
              action={
                <Link to="/missions/new" className="inline-flex items-center rounded-lg bg-gradient-to-b from-indigo-600 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-indigo-500/20 transition-all hover:shadow-md hover:shadow-indigo-500/30" data-testid="dashboard-post-mission-cta">
                  {t("dashboard.post_first_mission")}
                </Link>
              }
            />
          </>
        )}

        {user.role === "agency" && organization && (
          <>
            {billingSub && billingSub.promo_active ? (
              <PromoBanner promo={{
                active: true,
                end_date: billingSub.promo_end_date,
                days_remaining: billingSub.promo_days_remaining,
              }} />
            ) : (
              <TrialBanner sub={billingSub} />
            )}
            <AgencyBanner
              org={organization}
              stats={stats}
              isOnTrial={!!(billingSub && (billingSub.is_trial || billingSub.promo_active))}
            />
          </>
        )}

        {user.role === "admin" && (
          <StatusBanner
            tone="info"
            title={t("dashboard.welcome_admin")}
            body={t("admin_agencies.subtitle")}
            action={
              <Link
                to="/admin/approvals"
                className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                data-testid="admin-agencies-link"
              >
                {t("dashboard.admin_link_agencies")}
              </Link>
            }
          />
        )}
      </main>
    </div>
  );
}

function TrialBanner({ sub }) {
  const { t } = useTranslation();
  if (!sub || !sub.is_trial) return null;
  const days = sub.trial_days_remaining ?? 0;
  const urgent = days <= 3;
  const tone = urgent
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-emerald-200 bg-emerald-50 text-emerald-900";
  const key = urgent ? "dashboard.trial_urgent" : "dashboard.trial_nominal";
  return (
    <div
      className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border ${tone} px-5 py-4 shadow-sm`}
      data-testid="agency-trial-banner"
    >
      <div className="flex items-center gap-3">
        <span className="text-lg">{urgent ? "⚠️" : "🎁"}</span>
        <p className="text-sm">
          <span className="font-semibold">{t(`${key}_title`, { days })}</span>{" "}
          <span className="opacity-80">— {t(`${key}_body`)}</span>
        </p>
      </div>
      <Link
        to="/settings/subscription"
        className={
          "rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap " +
          (urgent
            ? "bg-amber-900 text-amber-50 hover:bg-amber-800"
            : "bg-emerald-700 text-white hover:bg-emerald-800")
        }
        data-testid="trial-banner-cta"
      >
        {t(urgent ? "dashboard.trial_urgent_cta" : "dashboard.trial_nominal_cta")}
      </Link>
    </div>
  );
}

function AgencyBanner({ org, stats, isOnTrial }) {
  const { t } = useTranslation();

  if (org.status === "pending_approval") {
    return <StatusBanner tone="warning" title={t("dashboard.agency_pending_title")} body={t("dashboard.agency_pending_body")} />;
  }
  if (org.status === "rejected") {
    return <StatusBanner tone="danger" title={t("dashboard.agency_rejected_title")} body={t("dashboard.agency_rejected_body")} />;
  }
  // If on free trial, don't show the "activate subscription" banner — the trial banner + stats already give context.
  if (isOnTrial) {
    return (
      <>
        {stats && (
          <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="agency-stats">
            <StatCard Icon={Target} label={t("dashboard.stat_market_matches")} value={stats.market_matches} testId="stat-market" href="/marketplace" hint={t("dashboard.stat_market_hint")} />
            <StatCard Icon={Briefcase} label={t("dashboard.stat_my_open_missions")} value={stats.my_open} testId="stat-my-open" href="/missions/mine" />
            <StatCard Icon={Users} label={t("dashboard.stat_roster")} value={stats.roster} testId="stat-roster" href="/roster" />
          </div>
        )}
      </>
    );
  }
  return (
    <>
      {stats && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="agency-stats">
          <StatCard Icon={Target} label={t("dashboard.stat_market_matches")} value={stats.market_matches} testId="stat-market" href="/marketplace" hint={t("dashboard.stat_market_hint")} />
          <StatCard Icon={Briefcase} label={t("dashboard.stat_my_open_missions")} value={stats.my_open} testId="stat-my-open" href="/missions/mine" />
          <StatCard Icon={Users} label={t("dashboard.stat_roster")} value={stats.roster} testId="stat-roster" href="/roster" />
        </div>
      )}
      <StatusBanner
        tone="info"
        title={t("dashboard.agency_no_sub_title")}
        body={t("dashboard.agency_no_sub_body")}
        action={
          <div className="flex flex-wrap gap-3">
            <Link
              to="/marketplace"
              className={
                "inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold " +
                (org.subscription_status === "active" || org.subscription_status === "trialing"
                  ? "bg-indigo-600 text-white hover:bg-indigo-700"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed pointer-events-none")
              }
              data-testid="dashboard-marketplace-cta"
              onClick={(e) => {
                if (!(org.subscription_status === "active" || org.subscription_status === "trialing")) {
                  e.preventDefault();
                }
              }}
            >
              {t("dashboard.browse_marketplace")}
            </Link>
            <Link to="/roster" className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="dashboard-agency-roster-cta">
              {t("dashboard.agency_active_cta")}
            </Link>
            <Link
              to="/settings/subscription"
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              data-testid="agency-subscribe-btn"
            >
              {t("dashboard.subscribe")}
            </Link>
          </div>
        }
      />
    </>
  );
}
